/**
 * "The socket as the previous step left it" — the free undo source.
 *
 * When a tool is the *first* thing applied on its step, the state to undo to is
 * exactly the finished mesh of the step before it. That mesh is already on the
 * server as a step checkpoint, uploaded for entirely unrelated reasons, so this
 * path costs no extra storage at all. It is also the only source available for
 * a step whose before-blobs were pruned after export.
 *
 * Walks backwards rather than taking the immediately-preceding step blindly:
 * a step may have no checkpoint slot, may have fallen back into a neighbour's
 * slot (`LEGACY_SLOT_FALLBACK`), or may simply never have uploaded because the
 * backend allowlist did not cover it. The first slot that actually has a mesh
 * wins.
 */
import { GLOBAL_STEP_ORDER, MAIN_PROCESS_STEPS } from '../../../shared/constants/flowConfig';
import {
  CHECKPOINT_SLOT_BY_STEP,
  LEGACY_SLOT_FALLBACK,
} from '../../../shared/constants/stepCheckpointSlots';
import { loadBkDesignOrderContext } from '../../../shared/lib/bkDesignOrderContext';
import {
  getCheckpointMeshUrl,
  getLockingBulldogEntry,
  getLockingPlainEntry,
} from '../../../shared/lib/bkScanSourceFiles';
import { installCheckpointSocket } from '../../../shared/lib/checkpointSocketInstall';
import useWorkflowStore from '../../../shared/stores/useWorkflowStore';

const LOG_PREFIX = '[PrevStepCheckpoint]';

/**
 * Checkpoint entry for one workflow step out of a `get_bk_design_files_meta`
 * payload, honouring the legacy slot fallbacks.
 *
 * @param {object} meta
 * @param {string} stepId
 */
export function checkpointEntryForStep(meta, stepId) {
  const checkpoints = meta?.checkpoints;
  if (!checkpoints) return null;

  if (stepId === MAIN_PROCESS_STEPS.LOCKING_MECHANISM) {
    return getLockingPlainEntry(meta) || getLockingBulldogEntry(meta) || null;
  }

  const slot = CHECKPOINT_SLOT_BY_STEP[stepId];
  const direct = slot ? checkpoints[slot] : null;
  if (getCheckpointMeshUrl(direct)) return direct;

  const fallback = LEGACY_SLOT_FALLBACK[stepId];
  if (!fallback) return null;
  const via = fallback.variant
    ? checkpoints[fallback.slot]?.[fallback.variant]
    : checkpoints[fallback.slot];
  return getCheckpointMeshUrl(via) ? via : null;
}

/**
 * Nearest step before `stepId` that has a downloadable checkpoint mesh.
 *
 * @param {object} meta
 * @param {string} stepId
 * @returns {{ stepId: string, entry: object } | null}
 */
export function findPreviousCheckpoint(meta, stepId) {
  const idx = GLOBAL_STEP_ORDER.indexOf(stepId);
  if (idx <= 0) return null;

  for (let i = idx - 1; i >= 0; i--) {
    const candidate = GLOBAL_STEP_ORDER[i];
    const entry = checkpointEntryForStep(meta, candidate);
    if (getCheckpointMeshUrl(entry)) return { stepId: candidate, entry };
  }
  return null;
}

/**
 * Is a previous-step checkpoint reachable for this step? Requires a network call,
 * so this is the *async* availability check — the synchronous button state uses
 * the manifest's recorded pointer instead.
 *
 * @param {string} stepId
 * @returns {Promise<boolean>}
 */
export async function hasPreviousCheckpoint(stepId) {
  const found = await resolvePreviousCheckpoint(stepId);
  return !!found;
}

/** @returns {Promise<{ stepId: string, entry: object } | null>} */
export async function resolvePreviousCheckpoint(stepId) {
  const ctx = loadBkDesignOrderContext();
  if (!ctx?.sales_order || !ctx?.customer) return null;
  try {
    const meta = await useWorkflowStore
      .getState()
      .actions.fetchScanFiles(ctx.sales_order, ctx.customer);
    return findPreviousCheckpoint(meta, stepId);
  } catch (e) {
    console.warn(`${LOG_PREFIX} meta fetch failed for ${stepId}`, e);
    return null;
  }
}

/**
 * Install the previous step's checkpoint as the editable socket.
 *
 * `preprocessed` is decided by what the mesh *is*, not by where it came from: the
 * locking/valve/logo checkpoints are CSG-derived manufacturing STLs whose vertex
 * weld and topology repair must be skipped, while every clinical checkpoint stays
 * fully processed so tools can keep editing it.
 *
 * @param {string} stepId  the step being undone (not the one being loaded)
 * @param {{ signal?: AbortSignal }} [opts]
 * @returns {Promise<{ ok: boolean, fromStepId?: string }>}
 */
export async function restorePreviousCheckpointSocket(stepId, { signal } = {}) {
  const found = await resolvePreviousCheckpoint(stepId);
  if (!found) {
    console.warn(`${LOG_PREFIX} no earlier checkpoint found before ${stepId}`);
    return { ok: false };
  }

  const preprocessed =
    found.stepId === MAIN_PROCESS_STEPS.LOCKING_MECHANISM ||
    found.stepId === MAIN_PROCESS_STEPS.VALVE ||
    found.stepId === MAIN_PROCESS_STEPS.LOGO_PLACEMENT;

  const ok = await installCheckpointSocket(found.entry, { preprocessed, signal });
  if (ok) {
    console.log(`${LOG_PREFIX} restored ${stepId} from the ${found.stepId} checkpoint`);
  }
  return { ok, fromStepId: found.stepId };
}
